import { z } from 'zod';
import {
  CASAS_DINHEIRO,
  CASAS_PERCENTUAL,
  compararDinheiro,
  desescalar,
  escalar,
  somarDinheiro,
} from './aritmetica.ts';
import {
  dataDoFatoSchema,
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  paginacaoSchema,
  respostaListaSchema,
  uuidSchema,
} from './comum.ts';
import { origemTituloSchema } from './contas-pagar.ts';
import { MAXIMO_PARCELAS_TITULO, numeroDocumentoSchema, situacaoTituloSchema } from './titulos.ts';

/**
 * Agrupamento de títulos a pagar (`.spec/015`, decisão 119; `PAG·05`).
 *
 * Títulos do mesmo fornecedor e da mesma empresa, **nunca pagos**, são
 * substituídos por um ou mais títulos novos, com vencimentos e valores
 * informados que fecham o total. É a renegociação com o fornecedor.
 *
 * Quatro escolhas do Adriano, e o que se segue de cada uma:
 *
 * - **Os originais são excluídos.** O agrupamento guarda a cópia de cada um, e
 *   é ela que trava a nota e o contrato de origem enquanto ele existir.
 * - **Só título sem pagamento.** O total a fechar é a soma dos valores, que é
 *   também a dos saldos — não há meia dívida a explicar.
 * - **Mesma empresa e mesmo fornecedor.** O título novo tem uma empresa só, e
 *   misturá-las faria uma dever a dívida da outra.
 * - **Desfazível enquanto nenhum título novo foi pago.** Corrigir é desfazer e
 *   refazer — o agrupamento não tem `PUT`.
 *
 * O rateio de obra e a classificação contábil **não se digitam**: saem dos
 * originais, ponderados pelo valor de cada um (`rateioCombinado`), e todo
 * título novo recebe os mesmos índices.
 */

/** Teto de títulos agrupados de uma vez. */
export const MAXIMO_TITULOS_AGRUPADOS = 200;

/* -------------------------------------------------------------------------- */
/* Payload                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Um título novo. A parcela **não** viaja: é a posição no array, e o total de
 * parcelas é o tamanho dele — "1/3, 2/3, 3/3", como o fornecedor vai cobrar.
 *
 * O número do documento também não: é **um só, no cabeçalho**, e as parcelas o
 * repetem. É como o título de nota — "4512" em 1/3, 2/3 e 3/3 — e foi pedido
 * pelo Adriano ao ver a tela: digitar o mesmo número em cada linha era o campo
 * que mais se preenchia e o que menos dizia.
 */
export const tituloDoAgrupamentoEntradaSchema = z.object({
  dataVencimento: dataSchema,
  /** Nulo recebe o vencimento, como no título manual. */
  dataPrevisaoPagamento: dataSchema.nullable().prefault(null),
  valor: dinheiroSchema.refine((v) => Number(v) > 0, 'O valor do título deve ser maior que zero.'),
});

export type TituloDoAgrupamentoEntrada = z.infer<typeof tituloDoAgrupamentoEntradaSchema>;

/**
 * Payload do agrupamento.
 *
 * O que ele deliberadamente **não** tem:
 *
 * - `empresaId` e `fornecedorId` — vêm dos títulos escolhidos, que o service
 *   confere serem todos do mesmo par. Aceitá-los daria duas respostas para a
 *   mesma pergunta.
 * - rateio e classificação — saem dos originais (`rateioCombinado`).
 * - `numero` — de `sequencias_documento`.
 *
 * O "fecha com o total" é conferido no service: o total é a soma dos títulos
 * escolhidos, que o payload não traz.
 */
export const criarAgrupamentoContaPagarSchema = z
  .object({
    dataLancamento: dataDoFatoSchema,
    /** O documento da renegociação — repetido em todas as parcelas novas. */
    numeroDocumento: numeroDocumentoSchema,
    observacao: z.string().trim().max(500).prefault(''),
    titulosIds: z
      .array(uuidSchema)
      .min(1, 'Escolha ao menos um título para agrupar.')
      .max(MAXIMO_TITULOS_AGRUPADOS, `São no máximo ${MAXIMO_TITULOS_AGRUPADOS} títulos.`),
    novosTitulos: z
      .array(tituloDoAgrupamentoEntradaSchema)
      .min(1, 'Informe ao menos um título novo.')
      .max(MAXIMO_PARCELAS_TITULO, `São no máximo ${MAXIMO_PARCELAS_TITULO} títulos novos.`),
  })
  .superRefine((dados, ctx) => {
    if (new Set(dados.titulosIds).size !== dados.titulosIds.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['titulosIds'],
        message: 'O mesmo título foi escolhido duas vezes.',
      });
    }

    dados.novosTitulos.forEach((titulo, indice) => {
      if (titulo.dataPrevisaoPagamento && titulo.dataPrevisaoPagamento < dados.dataLancamento) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['novosTitulos', indice, 'dataPrevisaoPagamento'],
          message: 'A previsão de pagamento não pode ser anterior ao lançamento.',
        });
      }
    });
  });

export type CriarAgrupamentoContaPagar = z.infer<typeof criarAgrupamentoContaPagarSchema>;

/* -------------------------------------------------------------------------- */
/* Regras compartilhadas                                                      */
/* -------------------------------------------------------------------------- */

/** Quanto os títulos novos somam, e quanto falta para o total. */
export function diferencaDoAgrupamento(
  total: string,
  novos: readonly { valor: string }[],
): { soma: string; diferenca: string; fecha: boolean } {
  const soma = somarDinheiro(novos.map((titulo) => titulo.valor || '0'));
  const diferenca = desescalar(
    escalar(total, CASAS_DINHEIRO) - escalar(soma, CASAS_DINHEIRO),
    CASAS_DINHEIRO,
  );

  return { soma, diferenca, fecha: compararDinheiro(diferenca, '0') === 0 };
}

/** Uma fatia de rateio de um título original. `chave` identifica o destino. */
export interface FatiaParaCombinar<C extends string = string> {
  chave: C;
  indiceRateio: string;
}

/**
 * O rateio combinado dos títulos originais: cada índice ponderado pelo valor do
 * título, sobre o total.
 *
 * O título de R$ 9.000,00 todo da Aurora e o de R$ 1.000,00 todo da Bela Vista
 * dão 90% e 10% — e não 50% e 50%, que distribuiria o custo pelo número de
 * títulos. É a mesma ponderação que a nota faz com os itens.
 *
 * **A soma fecha onde a soma exata fecha.** Cada fatia é arredondada para 4
 * casas pelo **maior resto**: a classificação, em que todo original soma 100%,
 * dá exatamente 100,0000%; o rateio de obra, que pode somar menos, dá a soma
 * exata arredondada uma vez. Arredondar fatia a fatia deixaria a classificação
 * em 99,9999% e o título novo seria recusado pelo próprio contrato.
 *
 * A fatia que arredonda para zero sai: o banco recusa índice zero, e uma obra
 * com 0,0000% não recebe custo nenhum.
 *
 * A ordem do resultado é a da primeira aparição de cada chave, e ela desempata
 * o maior resto — a mesma entrada dá sempre a mesma saída.
 */
export function rateioCombinado<C extends string>(
  originais: readonly { valor: string; fatias: readonly FatiaParaCombinar<C>[] }[],
): { chave: C; indiceRateio: string }[] {
  const total = originais.reduce((soma, o) => soma + escalar(o.valor, CASAS_DINHEIRO), 0n);
  if (total === 0n) return [];

  // Numerador exato de cada chave: Σ valor × índice, nas escalas de origem.
  const numeradores = new Map<C, bigint>();
  for (const original of originais) {
    const valor = escalar(original.valor, CASAS_DINHEIRO);
    for (const fatia of original.fatias) {
      const parcela = valor * escalar(fatia.indiceRateio, CASAS_PERCENTUAL);
      numeradores.set(fatia.chave, (numeradores.get(fatia.chave) ?? 0n) + parcela);
    }
  }

  const chaves = [...numeradores.keys()];
  const pisos = chaves.map((chave) => (numeradores.get(chave) ?? 0n) / total);
  const restos = chaves.map((chave) => (numeradores.get(chave) ?? 0n) % total);

  const somaExata = [...numeradores.values()].reduce((a, b) => a + b, 0n);
  // A soma alvo, arredondada meio para cima: é o total que as fatias devem dar.
  const alvo = (somaExata * 2n + total) / (total * 2n);
  let faltam = alvo - pisos.reduce((a, b) => a + b, 0n);

  const porResto = chaves
    .map((_, i) => i)
    .sort((a, b) => {
      const ra = restos[a] ?? 0n;
      const rb = restos[b] ?? 0n;
      if (ra === rb) return a - b;
      return ra > rb ? -1 : 1;
    });

  const finais = [...pisos];
  for (const i of porResto) {
    if (faltam <= 0n) break;
    finais[i] = (finais[i] ?? 0n) + 1n;
    faltam -= 1n;
  }

  return chaves
    .map((chave, i) => ({ chave, escalado: finais[i] ?? 0n }))
    .filter((fatia) => fatia.escalado > 0n)
    .map((fatia) => ({
      chave: fatia.chave,
      indiceRateio: desescalar(fatia.escalado, CASAS_PERCENTUAL),
    }));
}

/* -------------------------------------------------------------------------- */
/* Leitura                                                                    */
/* -------------------------------------------------------------------------- */

/** Um título substituído, como a cópia o guarda. */
export const origemDoAgrupamentoSchema = z.object({
  contaPagarId: uuidSchema,
  origem: origemTituloSchema,
  numeroDocumento: z.string(),
  parcela: z.number().int(),
  totalParcelas: z.number().int(),
  dataLancamento: dataSchema,
  dataVencimento: dataSchema,
  valor: dinheiroSchema,
  notaCompraId: uuidSchema.nullable(),
  contratoId: uuidSchema.nullable(),
});

export type OrigemDoAgrupamento = z.infer<typeof origemDoAgrupamentoSchema>;

/** Um título gerado, como ele está hoje. */
export const tituloDoAgrupamentoSchema = z.object({
  id: uuidSchema,
  numeroDocumento: z.string(),
  parcela: z.number().int(),
  totalParcelas: z.number().int(),
  dataVencimento: dataSchema,
  valor: dinheiroSchema,
  valorSaldo: dinheiroSchema,
  situacao: situacaoTituloSchema,
});

export const agrupamentoContaPagarSchema = z.object({
  id: uuidSchema,
  numero: z.number().int(),
  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),
  fornecedorId: uuidSchema,
  fornecedorNome: z.string().nullable(),
  dataLancamento: dataSchema,
  observacao: z.string(),
  /** Soma das origens — é também a soma dos títulos gerados. */
  valorTotal: dinheiroSchema,
  origens: z.array(origemDoAgrupamentoSchema),
  titulos: z.array(tituloDoAgrupamentoSchema),
  /**
   * `false` quando algum título gerado tem pagamento valendo, ou quando um deles
   * já entrou em outro agrupamento. A tela esconde o botão; o service recusa.
   */
  desfazivel: z.boolean(),
  createdAt: dataHoraSchema,
});

export type AgrupamentoContaPagar = z.infer<typeof agrupamentoContaPagarSchema>;

/** Linha da listagem: sem as listas, com as contagens. */
export const resumoDoAgrupamentoSchema = agrupamentoContaPagarSchema
  .omit({ origens: true, titulos: true, desfazivel: true })
  .extend({
    quantidadeOrigens: z.number().int(),
    quantidadeTitulos: z.number().int(),
  });

export type ResumoDoAgrupamento = z.infer<typeof resumoDoAgrupamentoSchema>;

export const listaAgrupamentosContaPagarSchema = respostaListaSchema(resumoDoAgrupamentoSchema);
export type ListaAgrupamentosContaPagar = z.infer<typeof listaAgrupamentosContaPagarSchema>;

export const listarAgrupamentosContaPagarQuerySchema = paginacaoSchema.extend({
  empresaId: uuidSchema.optional(),
  fornecedorId: uuidSchema.optional(),
});

export type ListarAgrupamentosContaPagarQuery = z.infer<
  typeof listarAgrupamentosContaPagarQuerySchema
>;
